import React, {
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import Barcode from 'react-barcode';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
} from 'antd';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

import './productTags.styles.less';
import moment from 'moment';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
  InputDecimal,
} from '@/components/_common';
import { useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { FaTimes } from 'react-icons/fa';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  pageSizesType,
  pageTypeSizeOptions,
  pageTypeSizes,
} from '@/types/PageSize';
import ItemsForLabelModalSelect from '@/components/_common/ItemsForLabelModalSelect';
import {
  ItemLabel,
  ItemLabelFilterType,
  ItemLabelRequest,
  ItemLabelResponse,
} from '@/core/domain/stock/itemsLabel/items-label.types';
import { useItemsForLabelList } from '@/core/domain/stock/itemsLabel/items-label.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

let prevSliceCount = 0;

export interface ProductTagFormValues {
  cols: number;
  gap: number;
  pageTypeSize: string;
  pageSize: { x: number; y: number };
  orientation: 'landscape' | 'portrait';
  lineBreak: number;
  noLineBreak: boolean;
  textSize: number;
  descriptionSize: number;
  withCardBorder: boolean;
  contentColumns: number;
  displayCodes: ('barcode' | 'sku' | 'manufacturingCode' | 'companyCode')[];
  displayBarcode: boolean;
  cardHeight: number;
  cardHeightAuto: boolean;
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  image: {
    display: false;
    blockade: true;
    width: number;
    height: number;
    position: 'top' | 'left';
  };
  textAlign: 'left' | 'center' | 'right';
}

const displayCodeLabels: Record<string, string> = {
  barcode: 'Cód. Barra',
  sku: 'Cód. Inter.',
  manufacturingCode: 'Cód. Fabr.',
  companyCode: 'Cód. Empre.',
  sellPrice: 'Preço',
  cost: 'Custo',
  realCost: 'Preço real',
  location: 'Localização',
};

const initialState = {
  cols: 3,
  gap: 5,
  lineBreak: 4,
  pageTypeSize: 'A4',
  pageSize: pageSizesType['A4'],
  orientation: 'portrait',
  noLineBreak: true,
  textSize: 16,
  descriptionSize: 30,
  withCardBorder: true,
  contentColumns: 1,
  displayCodes: ['manufacturingCode'],
  displayBarcode: true,
  cardHeightAuto: true,
  cardHeight: 60,
  margin: 10,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
  image: {
    display: false,
    blockade: true,
    width: 60,
    height: 30,
    position: 'top',
  },
  textAlign: 'left',
};

const priceFields = ['cost', 'sellPrice', 'realCost', 'location'];

const STORAGE_KEY = 'productTagsConfig';

const initialStorage = localStorage.getItem(STORAGE_KEY);
const initialFormValues = {
  ...initialState,
  ...(initialStorage ? JSON.parse(initialStorage) : {}),
};

const SLICE_COUNT = 50;

const PrivateReportProductTags: React.FC = () => {
  const match = useRouteMatch();

  const [drawerConfigOpen, drawerConfigAction] = useDisclosure(false);

  const [form] = Form.useForm<ProductTagFormValues | undefined>();
  const formValues = Form.useWatch([], form);

  const hasPageSizeTypeCustom =
    formValues?.pageTypeSize === pageTypeSizes['Custom'];
  const isLandscape = formValues?.orientation === 'landscape';

  const colWidth = 100 / (formValues?.cols || 0);

  const colWidthCalc = `calc(${colWidth}% - ${formValues?.gap || 0}mm)`;

  const hasBarcode = (formValues?.displayCodes || [])?.some(
    (code) => code === 'barcode',
  );
  const displayBarcode = !!formValues?.displayBarcode;

  document.title = `etiquetas_dos_produtos-${moment().format(
    'DDMMYYYY_HHmmss',
  )}`;

  const [parts, setParts] = useState<ItemLabel[]>([]);
  const [allSelecteds, setAllSelecteds] = useState(false);
  const [companyId, setCompanyId] = useState<number>();

  const [modalPartOpen, modalPartAction] = useDisclosure(false);

  const [queryParams, setQueryParams] = useState<ItemLabelRequest>();

  const itemsForLabelList = useItemsForLabelList(
    {
      page: 1,
      filterType: ItemLabelFilterType.Free,
      companyId,
      ...queryParams,
      pageSize: SLICE_COUNT,
    },
    allSelecteds,
  );

  const [lastItemsForLabel, setLastItemsForLabel] = useState(
    itemsForLabelList.data?.pages.at(-1),
  );

  const [loadingFetchAllPartPage, setLoadingFetchAllPartPage] = useState(false);

  const partsPaginated = itemsForLabelList.data?.pages.flatMap(
    (page) => page.result?.data || [],
  );

  useEffect(() => {
    if (partsPaginated?.length && allSelecteds) {
      setTotalTags(
        itemsForLabelList.data?.pages.at(-1)?.result?.totalRecords || 0,
      );
      setParts(partsPaginated);
    }
  }, [itemsForLabelList.dataUpdatedAt, allSelecteds]);

  useEffect(() => {
    if (!allSelecteds) {
      setTotalTags(parts.length);
    }
  }, [allSelecteds, parts]);

  useLayoutEffect(() => {
    const css = `
      @media print {
        .print-area {
          border: none !important;
          padding: 0px !important;
          width: 100% !important;
        }


        @page {
          size: ${
            hasPageSizeTypeCustom
              ? `${formValues?.pageSize[isLandscape ? 'y' : 'x'] || 0}mm ${
                  formValues?.pageSize[isLandscape ? 'x' : 'y'] || 0
                }mm`
              : formValues?.pageTypeSize || 'auto'
          } ${!hasPageSizeTypeCustom ? formValues?.orientation : ''};

          margin-top: ${formValues?.marginTop || 0}mm;
          margin-bottom: ${formValues?.marginBottom || 0}mm;
          margin-left: ${formValues?.marginLeft || 0}mm;
          margin-right: ${formValues?.marginRight || 0}mm;
        }
    `;

    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);

    style.setAttribute('type', 'text/css');
    style.setAttribute('id', 'product-tag-style-print');

    style.appendChild(document.createTextNode(css));

    return () => document.getElementById('product-tag-style-print')?.remove();
  }, [formValues]);

  useLayoutEffect(() => {
    if (!formValues?.noLineBreak && parts.length) {
      const printAreaElem = document.getElementById('print-area');

      if (!!printAreaElem?.firstChild?.childNodes.length) {
        Array.from(printAreaElem.firstChild?.childNodes)
          .filter(
            (item) =>
              !(item as HTMLDivElement).classList.contains('break-line'),
          )
          .forEach((_, i, items) => {
            if (
              !(
                (i + 1) %
                ((formValues?.cols || 0) * (formValues?.lineBreak || 0))
              )
            ) {
              const breakLineDiv = document.createElement('div');

              breakLineDiv.classList.add('break-line');
              breakLineDiv.style.marginBottom = `${formValues?.gap}mm`;

              printAreaElem.firstChild?.insertBefore(
                breakLineDiv,
                items[i + 1],
              );
            }
          });
      }
    }

    return () => {
      document.querySelectorAll('.break-line').forEach((node) => node.remove());
    };
  }, [parts, formValues]);

  function removePart(part: ItemLabel) {
    setParts((prev) =>
      prev.filter((prevPart) => prevPart.partId !== part.partId),
    );
  }

  async function validateFields() {
    await form.validateFields();
  }

  const [sliceCount, setSliceCount] = useState(SLICE_COUNT);

  useEffect(() => {
    const body = document.querySelector('body');

    const onScroll = async () => {
      if (body) {
        const bodyElement = body.getBoundingClientRect();
        const scrollHeight = body.scrollHeight - bodyElement.height;
        const scrollTop = Math.abs(bodyElement.top) + 200;

        const nextSliceCount = sliceCount + SLICE_COUNT;

        if (scrollTop >= scrollHeight) {
          if (allSelecteds) {
            const currLastPage = lastItemsForLabel?.result?.page || 1;
            const pagesCount = itemsForLabelList.data?.pages.length || 1;

            if (itemsForLabelList.isFetching || currLastPage >= pagesCount)
              return;

            if (sliceCount < parts.length) {
              const lastPageIndex = Math.ceil(nextSliceCount / SLICE_COUNT) - 1;

              setSliceCount(nextSliceCount);

              if (lastPageIndex >= 0) {
                setLastItemsForLabel(
                  itemsForLabelList.data?.pages[lastPageIndex],
                );
              }
            } else {
              const result = await itemsForLabelList.fetchNextPage();

              setLastItemsForLabel(result.data?.pages.at(-1));
              setSliceCount(nextSliceCount);
            }
          } else {
            setSliceCount(nextSliceCount);
          }
        }
      }
    };

    document.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [
    sliceCount,
    allSelecteds,
    itemsForLabelList.isFetching,
    itemsForLabelList.hasNextPage,
    lastItemsForLabel,
    parts,
  ]);

  window.onafterprint = () => {
    window.scrollTo({ top: 0 });

    setTimeout(() => {
      const lastPageIndex = Math.ceil(prevSliceCount / SLICE_COUNT) - 1;

      if (lastPageIndex >= 0) {
        setLastItemsForLabel(itemsForLabelList.data?.pages[lastPageIndex]);
      }

      setSliceCount(prevSliceCount);
    }, 500);
  };

  const startPrint = useCallback(async () => {
    if (allSelecteds) {
      const lastItemForLabel = itemsForLabelList.data?.pages.at(-1);
      setLoadingFetchAllPartPage(true);
      await fetchAllPartPages(lastItemForLabel);

      prevSliceCount = sliceCount;
      setSliceCount(lastItemForLabel?.result?.totalRecords || 0);

      setTimeout(window.print, 500);

      setLoadingFetchAllPartPage(false);
    } else {
      prevSliceCount = sliceCount;
      setSliceCount(parts.length);

      setTimeout(window.print, 500);
    }
  }, [itemsForLabelList.dataUpdatedAt, sliceCount]);

  const fetchAllPartPages = useCallback(
    async (data?: ItemLabelResponse): Promise<void> => {
      const totalPages = data?.result?.totalPages || 1;
      const page = data?.result?.page || 1;

      if (page < totalPages) {
        const result = await itemsForLabelList.fetchNextPage();

        const lastResult = result.data?.pages.at(-1);

        setLastItemsForLabel(lastResult);

        if (result.hasNextPage) {
          return await new Promise((res, rej) => {
            setTimeout(() => {
              fetchAllPartPages(lastResult).then(res).catch(rej);
            }, 0);
          });
        }
      }

      return;
    },
    [itemsForLabelList.dataUpdatedAt],
  );

  const [modalRepeatTagOpened, modalRepeatTagAction] = useDisclosure(false);
  const [tagItemSelected, setTagItemSelected] = useState<ItemLabel>();
  const [tagRepeatQtd, setTagRepeatQtd] = useState<number>(0);
  const [totalTags, setTotalTags] = useState(0);

  function openRepeatTag(item: ItemLabel) {
    modalRepeatTagAction.open();
    setTagItemSelected(item);
  }

  function repeatTags() {
    if (tagRepeatQtd && tagItemSelected) {
      setParts((prev) => [
        ...prev,
        ...Array.from({ length: tagRepeatQtd }).map((_, i) => ({
          ...tagItemSelected,
          partId: tagItemSelected.partId * (1000 + i),
        })),
      ]);

      setTagRepeatQtd(0);
      setTagItemSelected(undefined);
      modalRepeatTagAction.close();
    }
  }

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {match.isExact && (
        <TemplateReport style={{ margin: '0px', width: '100%' }}>
          <>
            {(itemsForLabelList.isFetching || loadingFetchAllPartPage) &&
              !!partsPaginated?.length && (
                <ModalLoadingOverlay
                  key={partsPaginated.length}
                  messages={[
                    `Carregando etiquetas (${partsPaginated.length}/${
                      lastItemsForLabel?.result?.totalRecords || 0
                    })... `,
                  ]}
                />
              )}

            <Form
              form={form}
              initialValues={initialFormValues}
              onValuesChange={(_, values) =>
                localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
              }
            >
              <main className="report-product-tags">
                <div className="display-none-to-print">
                  <div className="report-product-tags-header">
                    <Row justify="space-between" gutter={[20, 20]}>
                      <Col span={24} md={6}>
                        <h2 style={{ margin: 0, padding: 0 }}>
                          Gerar etiquetas de peças
                        </h2>
                      </Col>

                      <Col span={24} md={18}>
                        <SpaceForButton justifyContent="flex-end">
                          <Button block onClick={drawerConfigAction.open}>
                            Configurar impressão
                          </Button>

                          <Button
                            block
                            className="button-primary"
                            type="primary"
                            onClick={startPrint}
                            disabled={!parts.length}
                          >
                            Imprimir
                          </Button>
                        </SpaceForButton>
                      </Col>
                    </Row>
                  </div>
                </div>

                {!!companyId && (
                  <div className="display-none-to-print">
                    <div className="report-product-tags-header">
                      <Row justify="space-between" gutter={[20, 20]}>
                        <Col span={24} md={16} lg={14}>
                          <Row justify="space-between" gutter={[20, 20]}>
                            <Col span={24} md={12}>
                              <CompaniesInfiniteSelectField
                                value={companyId}
                                onChange={setCompanyId}
                                style={{ width: '100%' }}
                              />
                            </Col>
                            <Col span={24} md={12}>
                              Etiquetas: {totalTags}
                            </Col>
                          </Row>
                        </Col>

                        <Col span={24} md={8} lg={10}>
                          <SpaceForButton justifyContent="flex-end">
                            <Button
                              block
                              onClick={modalPartAction.open}
                              disabled={!companyId || modalPartOpen}
                              type="primary"
                              className="button-primary"
                            >
                              Selecionar peças
                            </Button>
                          </SpaceForButton>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                <div className="report-product-tags-content">
                  {!companyId && (
                    <Group position="center">
                      <div className="card">
                        <p>
                          Selecione uma empresa para filtrar os preços das peças
                        </p>

                        <CompaniesInfiniteSelectField
                          placeholder="Empresa"
                          value={companyId}
                          onChange={setCompanyId}
                        />
                      </div>
                    </Group>
                  )}

                  {!!companyId && !parts.length && (
                    <Empty
                      description={
                        <>
                          <p>
                            Selecione as peças para compor a lista de etiquetas
                          </p>

                          <Button
                            type="primary"
                            className="button-primary"
                            onClick={modalPartAction.open}
                          >
                            Selecionar peças
                          </Button>
                        </>
                      }
                    />
                  )}

                  {!!companyId && !!parts.length && (
                    <>
                      <div className="print-page-info">
                        <span>
                          (
                          {hasPageSizeTypeCustom
                            ? 'Customizado'
                            : formValues?.pageTypeSize}
                          ){' '}
                          {`${
                            isLandscape
                              ? formValues?.pageSize?.y || 0
                              : formValues?.pageSize?.x || 0
                          }mmX${
                            isLandscape
                              ? formValues?.pageSize?.x || 0
                              : formValues?.pageSize?.y || 0
                          }mm`}{' '}
                          {formValues?.orientation === 'portrait'
                            ? 'Retrato'
                            : 'Paisagem'}
                        </span>
                      </div>

                      <div
                        className="print-area"
                        id="print-area"
                        style={{
                          paddingTop: `${formValues?.marginTop || 0}mm`,
                          paddingRight: `${formValues?.marginRight || 0}mm`,
                          paddingBottom: `${formValues?.marginBottom || 0}mm`,
                          paddingLeft: `${formValues?.marginLeft || 0}mm`,
                          width: formValues?.pageSize
                            ? `${
                                isLandscape
                                  ? formValues?.pageSize?.y || 0
                                  : formValues?.pageSize?.x || 0
                              }mm`
                            : undefined,
                        }}
                      >
                        <div
                          className="row"
                          style={{
                            margin: `0 -${(formValues?.gap || 0) / 2}mm`,
                            flexWrap: 'wrap',
                          }}
                        >
                          {parts?.slice(0, sliceCount)?.map((part, i) => {
                            const itemPrices = part?.price;

                            const infoFields = (
                              formValues?.displayCodes || []
                            )?.filter((code) =>
                              displayBarcode
                                ? code !== 'barcode' &&
                                  !priceFields.includes(code)
                                : !priceFields.includes(code),
                            );

                            const contentColumns =
                              formValues?.contentColumns || 1;
                            const hasOneColumn = contentColumns === 1;

                            const image = formValues?.image;
                            const displayImage = image?.display;
                            const isImageBlockade = image?.blockade;
                            const imagePositionTop = image?.position === 'top';

                            return (
                              <div
                                key={part?.partId}
                                style={{
                                  margin: `0 ${(formValues?.gap || 0) / 2}mm 
                                ${formValues?.gap || 0}mm 
                                ${(formValues?.gap || 0) / 2}mm`,
                                  width: `${colWidthCalc}`,
                                  cursor: 'pointer',
                                }}
                                className="tag-wrapper"
                                id={`part-${part?.partId}`}
                                onClick={() => openRepeatTag(part)}
                              >
                                {!allSelecteds && (
                                  <button
                                    className="remove-tag"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removePart(part);
                                    }}
                                  >
                                    <FaTimes className="remove-tag-icon" />
                                  </button>
                                )}

                                <div
                                  className="card w-100 tag-card"
                                  style={{
                                    padding: 10,
                                    fontSize: formValues?.textSize,
                                    height: formValues?.cardHeightAuto
                                      ? 'auto'
                                      : `${formValues?.cardHeight}mm` || 'auto',
                                    borderColor: !formValues?.withCardBorder
                                      ? 'transparent'
                                      : undefined,
                                    textAlign: formValues?.textAlign || 'left',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 8,
                                      flexDirection: imagePositionTop
                                        ? 'column'
                                        : 'row',
                                    }}
                                  >
                                    {displayImage && (
                                      <img
                                        src={part.imageUrl || undefined}
                                        style={{
                                          display: 'block',
                                          width: isImageBlockade
                                            ? '100%'
                                            : `${image?.width}mm`,
                                          height: `${image?.height}mm`,
                                          objectFit: 'cover',
                                          borderRadius: 4,
                                          border: 'none',
                                          backgroundColor: '#fafafa',
                                        }}
                                      />
                                    )}

                                    <div>
                                      <p>
                                        <strong>
                                          {part.description?.slice(
                                            0,
                                            formValues?.descriptionSize,
                                          )}
                                        </strong>
                                      </p>

                                      <Row gutter={hasOneColumn ? 0 : 5}>
                                        {infoFields?.map((code) => (
                                          <Col
                                            key={code}
                                            span={24 / contentColumns}
                                          >
                                            <div
                                              style={{
                                                display: hasOneColumn
                                                  ? 'inline'
                                                  : 'block',
                                              }}
                                            >
                                              {displayCodeLabels[code]}:{' '}
                                            </div>
                                            {part[code] || 'N/A'}
                                          </Col>
                                        ))}

                                        {(formValues?.displayCodes || []).some(
                                          (key: string) => key === 'location',
                                        ) && (
                                          <Col
                                            span={24 / contentColumns}
                                            key="location"
                                          >
                                            <div
                                              style={{
                                                display: hasOneColumn
                                                  ? 'inline'
                                                  : 'block',
                                              }}
                                            >
                                              {displayCodeLabels.location}:{' '}
                                            </div>
                                            {itemPrices?.location || 'N/A'}
                                          </Col>
                                        )}
                                      </Row>

                                      {!!itemPrices && (
                                        <p>
                                          <Group gap={4}>
                                            {(formValues?.displayCodes || [])
                                              .filter(
                                                (key: string) =>
                                                  priceFields.includes(key) &&
                                                  key !== 'location',
                                              )
                                              .map((key: string) => (
                                                <strong key={key}>
                                                  {formatterPrice(
                                                    Number(
                                                      itemPrices[
                                                        key as keyof typeof itemPrices
                                                      ],
                                                    ),
                                                  )}
                                                </strong>
                                              ))}
                                          </Group>
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {hasBarcode && displayBarcode && (
                                    <Group
                                      position="center"
                                      style={{ marginTop: 5 }}
                                    >
                                      {part?.barcode?.toUpperCase() !==
                                      'SEM GTIN' ? (
                                        <Barcode
                                          value={part.barcode}
                                          height={formValues?.textSize * 1.5}
                                          fontSize={formValues?.textSize - 2}
                                        />
                                      ) : (
                                        'SEM GTIN'
                                      )}
                                    </Group>
                                  )}
                                </div>

                                {!formValues?.noLineBreak && (
                                  <>
                                    {!(
                                      (i + 1) %
                                      ((formValues?.cols || 0) *
                                        (formValues?.lineBreak || 0))
                                    ) && <div className="page-break" />}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </main>

              <Drawer
                title="Configuração de impressão"
                placement="right"
                open={drawerConfigOpen}
                onClose={drawerConfigAction.close}
                destroyOnClose={false}
                width={600}
              >
                <>
                  <div className="m-b-20">
                    <strong>Layout da página</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Tipo de impressão"
                        name="pageTypeSize"
                        getValueFromEvent={(value) => {
                          if (value !== pageTypeSizes['Custom']) {
                            form.setFieldsValue({
                              pageSize: pageSizesType[value],
                            });
                          }
                          validateFields();
                          return value;
                        }}
                      >
                        <Select
                          options={pageTypeSizeOptions}
                          style={{ width: '100%' }}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Orientação" name="orientation">
                        <Select
                          options={[
                            { value: 'landscape', label: 'Paisagem' },
                            { value: 'portrait', label: 'Retrato' },
                          ]}
                          style={{ width: '100%' }}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Largura da página (mm)"
                        name={['pageSize', 'x']}
                        rules={[
                          {
                            type: 'number',
                            required: formValues?.pageTypeSize === 'Custom',
                            message: '',
                          },
                        ]}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          disabled={!hasPageSizeTypeCustom}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Altura da página (mm)"
                        name={['pageSize', 'y']}
                        rules={[
                          {
                            type: 'number',
                            required: formValues?.pageTypeSize === 'Custom',
                            message: '',
                          },
                        ]}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          disabled={!hasPageSizeTypeCustom}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Colunas" name="cols">
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={1}
                          max={10}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Espaço entre colunas (mm)"
                        name="gap"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '0'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        name="noLineBreak"
                        initialValue={true}
                        valuePropName="checked"
                        style={{ marginTop: 12 }}
                      >
                        <Checkbox>Quebra automática de linha</Checkbox>
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Linha de quebra"
                        name="lineBreak"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          disabled={formValues?.noLineBreak}
                          min={1}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>
                  </Row>

                  <Divider />

                  <div className="m-b-20">
                    <strong>Conteúdo</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <FloatingFormItem
                        label="Alinhamento conteúdo"
                        name="textAlign"
                      >
                        <Select
                          style={{ width: '100%' }}
                          options={[
                            { value: 'left', label: 'Esquerda' },
                            {
                              value: 'center',
                              label: 'Centralizado',
                            },
                            {
                              value: 'right',
                              label: 'Direita',
                            },
                          ]}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Tamanho do texto (px)"
                        name="textSize"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={10}
                          max={24}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '10'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Caracteres da descrição"
                        name="descriptionSize"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={1}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Colunas do conteúdo"
                        name="contentColumns"
                      >
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <FloatingFormItem
                        name="displayBarcode"
                        initialValue={true}
                        valuePropName="checked"
                      >
                        <Checkbox>Exibir imagem do código de barras</Checkbox>
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <FloatingFormItem
                        label="Campos de exibição"
                        name="displayCodes"
                        getValueFromEvent={(values) => {
                          return values.length ? values : undefined;
                        }}
                      >
                        <Select
                          mode="multiple"
                          style={{ width: '100%' }}
                          options={[
                            { value: 'barcode', label: 'Cód. de Barras' },
                            {
                              value: 'sku',
                              label: 'Cód. Interno',
                            },
                            {
                              value: 'manufacturingCode',
                              label: 'Cód. Fabricante',
                            },
                            { value: 'companyCode', label: 'Cód. Empresa' },
                            { value: 'cost', label: 'Preço de Custo' },
                            { value: 'sellPrice', label: 'Preço de Venda' },
                            { value: 'realCost', label: 'Custo Real' },
                            { value: 'location', label: 'Localização' },
                          ]}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <div className="m-b-20">
                        <strong>Imagem</strong>
                      </div>

                      <Row gutter={[16, 16]}>
                        <Col span={24} md={12}>
                          <FloatingFormItem
                            name={['image', 'display']}
                            initialValue={false}
                            valuePropName="checked"
                          >
                            <Checkbox>Exibir imagem da peça</Checkbox>
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <FloatingFormItem
                            name={['image', 'blockade']}
                            initialValue={false}
                            valuePropName="checked"
                          >
                            <Checkbox>Imagem responsiva</Checkbox>
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <FloatingFormItem
                            label="Largura da imagem (mm)"
                            name={['image', 'width']}
                          >
                            <InputNumber<number>
                              style={{ width: '100%' }}
                              min={1}
                            />
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <FloatingFormItem
                            label="Altura da imagem (mm)"
                            name={['image', 'height']}
                          >
                            <InputNumber<number>
                              style={{ width: '100%' }}
                              min={1}
                            />
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <Form.Item
                            label="Posição da imagem"
                            name={['image', 'position']}
                            initialValue="top"
                          >
                            <Radio.Group>
                              <Radio.Button value="top">Acima</Radio.Button>
                              <Radio.Button value="left">Esquerda</Radio.Button>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>

                    <Col span={24}>
                      <div className="m-b-20">
                        <strong>Etiqueta (card)</strong>
                      </div>

                      <Row gutter={[16, 16]}>
                        <Col span={24} md={12}>
                          <FloatingFormItem
                            name="cardHeightAuto"
                            initialValue={true}
                            valuePropName="checked"
                            style={{ marginTop: 12 }}
                          >
                            <Checkbox>Altura automática</Checkbox>
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <FloatingFormItem
                            label="Altura da etiqueta (mm)"
                            name="cardHeight"
                          >
                            <InputDecimal
                              disabled={formValues?.cardHeightAuto}
                              style={{ width: '100%' }}
                              min={0}
                            />
                          </FloatingFormItem>
                        </Col>

                        <Col span={24}>
                          <FloatingFormItem
                            name="withCardBorder"
                            initialValue={true}
                            valuePropName="checked"
                          >
                            <Checkbox>Aplicar borda a etiqueta</Checkbox>
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Divider />
                  <div className="m-b-20">
                    <strong>Margens da folha</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <FloatingFormItem
                        label="Todo os lados (mm)"
                        name="allMarginSides"
                        getValueFromEvent={(value) => {
                          form.setFieldsValue({
                            marginBottom: value,
                            marginLeft: value,
                            marginRight: value,
                            marginTop: value,
                          });
                          return value;
                        }}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <Row justify="center">
                        <Col span={12}>
                          <FloatingFormItem
                            label="Superiro (mm)"
                            name="marginTop"
                          >
                            <InputDecimal
                              style={{ width: '100%' }}
                              min={0}
                              max={50}
                            />
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Esquerda (mm)" name="marginLeft">
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Direita (mm)" name="marginRight">
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <Row justify="center">
                        <Col span={12}>
                          <FloatingFormItem
                            label="Inferiro (mm)"
                            name="marginBottom"
                          >
                            <InputDecimal
                              style={{ width: '100%' }}
                              min={0}
                              max={50}
                            />
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </>

                <Divider />

                <div className="report-product-tags-drawer-info m-b-20">
                  <strong>
                    <small>Legendas</small>
                  </strong>

                  <span className="-red">Área de impressão</span>

                  <span className="-gray">Quebra de página</span>
                </div>
              </Drawer>

              <Form.Item hidden name="cols" />
              <Form.Item hidden name="gap" />
              <Form.Item hidden name="pageTypeSize" />
              <Form.Item hidden name="pageSize" />
              <Form.Item hidden name="orientation" />
              <Form.Item hidden name="lineBreak" />
              <Form.Item hidden name="noLineBreak" />
              <Form.Item hidden name="textSize" />
              <Form.Item hidden name="descriptionSize" />
              <Form.Item hidden name="contentColumns" />
              <Form.Item hidden name="withCardBorder" />
              <Form.Item hidden name="displayCodes" />
              <Form.Item hidden name="displayBarcode" />
              <Form.Item hidden name="cardHeight" />
              <Form.Item hidden name="cardHeightAuto" />
              <Form.Item hidden name="margin" />
              <Form.Item hidden name="marginTop" />
              <Form.Item hidden name="marginBottom" />
              <Form.Item hidden name="marginLeft" />
              <Form.Item hidden name="marginRight" />
              <Form.Item hidden name="textAlign" />
            </Form>

            <ItemsForLabelModalSelect
              opened={modalPartOpen}
              onClose={modalPartAction.close}
              onPartsSelect={setParts}
              onAllPartsSelected={(value) => {
                setAllSelecteds(value);
                if (!value && allSelecteds) {
                  itemsForLabelList.remove();
                }
              }}
              allPartsSelected={allSelecteds}
              companyId={companyId}
              partsSelected={parts}
              onFilterParts={setQueryParams}
            />

            <Modal
              title="Repetição de etiqueta"
              okText="Repetir etiquetas"
              onOk={repeatTags}
              open={modalRepeatTagOpened}
              onCancel={() => {
                setTagItemSelected(undefined);
                setTagRepeatQtd(0);
                modalRepeatTagAction.close();
              }}
            >
              <p>
                Informe a quantidade de repetição da etiqueta "
                {tagItemSelected?.description}"
              </p>

              <InputNumber value={tagRepeatQtd} onChange={setTagRepeatQtd} />
            </Modal>
          </>
        </TemplateReport>
      )}
    </React.Suspense>
  );
};

export default PrivateReportProductTags;
